import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { billing, platform } from '@platform/contracts';
import { useApi } from '@platform/app-kit';

export const keys = {
  tenants: ['platform', 'tenants'] as const,
  tenant: (id: string) => ['platform', 'tenants', id] as const,
  packages: ['platform', 'packages'] as const,
  products: ['platform', 'products'] as const,
  subscriptions: ['billing', 'subscriptions'] as const,
  subscription: (id: string) => ['billing', 'subscriptions', id] as const,
  announcements: ['platform', 'announcements'] as const,
  cells: ['platform', 'cells'] as const,
  usage: (period: string, tenantId?: string) =>
    ['platform', 'metering', period, tenantId ?? '*'] as const,
};

export function useTenants() {
  const api = useApi();
  return useQuery({
    queryKey: keys.tenants,
    queryFn: async () => (await api.platform.Tenants_list({ query: { limit: 200 } })).items,
  });
}

export function useTenant(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.tenant(id),
    queryFn: async () => (await api.platform.Tenants_get({ params: { tenantId: id } })).data,
    enabled: id.length > 0,
  });
}

export function usePackages(includeRetired = false) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.packages, includeRetired] as const,
    queryFn: async () =>
      (await api.platform.Packages_list({ query: { includeRetired, limit: 200 } })).items,
  });
}

/** Every product the platform sells; products change only by a deploy, so this rarely refetches. */
export function useProducts() {
  const api = useApi();
  return useQuery({
    queryKey: keys.products,
    queryFn: async () => (await api.platform.Products_list()).items,
    staleTime: Infinity,
  });
}

/** Metered usage per tenant for one UTC month (`YYYY-MM`) or day (`YYYY-MM-DD`). */
export function useMeteringUsage(period: string, tenantId?: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.usage(period, tenantId),
    queryFn: async () =>
      (
        await api.platform.Metering_usage({
          query: { period, limit: 200, ...(tenantId ? { tenantId } : {}) },
        })
      ).items,
    // Spend moves with every call; keep the numbers fresh while the screen is open.
    refetchInterval: 60_000,
  });
}

export function useSubscriptions() {
  const api = useApi();
  return useQuery({
    queryKey: keys.subscriptions,
    queryFn: async () => (await api.billing.Subscriptions_list({ query: { limit: 200 } })).items,
  });
}

export function useSubscription(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.subscription(tenantId),
    queryFn: async () => (await api.billing.Subscriptions_get({ params: { tenantId } })).data,
    enabled: tenantId.length > 0,
  });
}

function useTenantWrite<TVars>(run: (vars: TVars) => Promise<platform.Tenant>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (tenant) => {
      qc.setQueryData(keys.tenant(tenant.tenantId), tenant);
      return qc.invalidateQueries({ queryKey: keys.tenants });
    },
  });
}

export function useSavePackage() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input:
        | { kind: 'create'; body: platform.PackageCreate }
        | { kind: 'update'; packageId: string; body: platform.PackageUpdate },
    ) =>
      input.kind === 'create'
        ? (await api.platform.Packages_create({ body: input.body })).data
        : (
            await api.platform.Packages_update({
              params: { packageId: input.packageId },
              body: input.body,
            })
          ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.packages }),
  });
}

export function useRetirePackage() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (packageId: string) =>
      (await api.platform.Packages_retire({ params: { packageId } })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.packages }),
  });
}

export function useReapplyPackage() {
  const api = useApi();
  return useTenantWrite(
    async (tenantId: string) =>
      (await api.platform.Tenants_reapplyPackage({ params: { tenantId } })).data,
  );
}

export function useAdoptPackage() {
  const api = useApi();
  return useTenantWrite(
    async ({ tenantId, packageId }: { tenantId: string; packageId: string }) =>
      (await api.platform.Tenants_adoptPackage({ params: { tenantId }, body: { packageId } })).data,
  );
}

export function useSuspendTenant() {
  const api = useApi();
  return useTenantWrite(
    async (tenantId: string) => (await api.platform.Tenants_suspend({ params: { tenantId } })).data,
  );
}

export function useResumeTenant() {
  const api = useApi();
  return useTenantWrite(
    async (tenantId: string) => (await api.platform.Tenants_resume({ params: { tenantId } })).data,
  );
}

export function useAdjustSubscription() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      body,
    }: {
      tenantId: string;
      body: billing.SubscriptionAdjust;
    }) => (await api.billing.Subscriptions_adjust({ params: { tenantId }, body })).data,
    onSuccess: (record) => {
      qc.setQueryData(keys.subscription(record.tenantId), record);
      return qc.invalidateQueries({ queryKey: keys.subscriptions });
    },
  });
}

/** What staff have said to people in the apps (ADR in-app-notices-from-staff), newest first. */
export function useAnnouncements() {
  const api = useApi();
  return useQuery({
    queryKey: keys.announcements,
    queryFn: async () => (await api.platform.Announcements_list({ query: { limit: 100 } })).items,
  });
}

export function useCells() {
  const api = useApi();
  return useQuery({
    queryKey: keys.cells,
    queryFn: async () => (await api.platform.Cells_list()).items,
  });
}

export function usePublishAnnouncement() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: platform.AnnouncementCreate) =>
      (await api.platform.Announcements_publish({ body })).data,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.announcements }),
  });
}

export function useWithdrawAnnouncement() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (announcementId: string) =>
      (await api.platform.Announcements_withdraw({ params: { announcementId } })).data,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.announcements }),
  });
}
